import _ from 'lodash'
import {KeyValuePair, Machine} from "@/modules/unigest";
import * as wamp  from '@/modules/wamp'
import {ISubscription} from "autobahn";
import * as log from '@/modules/logs'

let connected = false
let _machine: Machine | null = null
let _serial = ''


export interface SignatureData {
    nif?: string;
}

export class SignatureValidation {
    valid = false
    reason = ''
    documento = ''
    importe = 0

    public static deserialize (data: KeyValuePair) {
        return Object.assign(new this(), {
            valid: data.validado,
            reason: data.mensaje,
            documento: data.documento,
            importe: data.importe
        })
    }
}

/**
 * Eventos del protocolo WAMP lanzados o escuchados por el módulo.
 */
enum Event {
    Init = 'iniciar',
    Clear = 'limpiar',
    Finish = 'finalizar',
    SignatureUpdated = 'firma_actualizada',
    Data = 'info',
    OnValidation = 'validacion'
}

/**
 * Solicitud de firma
 */
export class SignatureRequest {
    public readonly createdAt: Date
    public readonly documento: string
    public readonly importe: number

    constructor () {
        this.createdAt = new Date()
        this.documento = "";
        this.importe = 0;
    }
}

export interface InitOptions {
    machine: Machine;
    serial: string;
}

/**
 * Construye una URI completa según el nombre del evento WAMP
 * argumentado
 *
 * @param event
 */
function uri (event: Event) {
    return `net.unigest.firmas.${event}.${_serial}_${_machine?.name}`
}

async function subscribe (event: Event, handler: (...args: any) => void) {
    return wamp.subscribe(uri(event), handler)
}

export function isConnected () {
    return wamp.isConnected()
}

/**
 * Inicia una comunicación con la m
 * @param options
 */
export async function init (options: InitOptions) {
    if (connected) {
        return
    }

    await wamp.connect()
    _machine = options.machine
    _serial = options.serial
    connected = true
}

/**
 * Envía la firma a la máquina que se encuentra a la escucha
 */
export function send (signature: string) {
    if (!connected) {
        throw new Error('El dispositivo no se encuentra conectado a la maquina')
    }

    return wamp.publish(uri(Event.SignatureUpdated),[signature])
}

/**
 * Envía a Unigest información relacionada sobre la firma que se esta realizando
 *
 * @param data
 */
export function sendData (data: SignatureData) {
    if (!connected) {
        throw new Error('El dispositivo no se encuentra conectado a la maquina')
    }

    return wamp.publish(uri(Event.Data), [data])
}

/**
 * Ejecuta la función argumentada en el parámetro "handler" cada vez que la
 * maquina de firmas solicita limpiar el area de firmado
 *
 * @param handler
 */
export function onClear (handler: () => void) {
    return subscribe(Event.Clear, handler)
}

/**
 * Permite suscribirse al evento lanzado por la maquina de firmas indicado
 * que el proceso de firmas ha terminado
 *
 * @param handler
 */
export function onFinish (handler: () => void) {
    return subscribe(Event.Finish, handler)
}

/**
 * Permite suscribirse al evento lanzado por la máquina de firmas indicado
 * que el proceso de firma ha iniciado
 *
 * @param handler
 */
export function onSignature (handler: (request: SignatureRequest[]) => void) {
    return subscribe(Event.Init, handler)
}

/**
 * Permite suscribirse a los eventos lanzados por Unigest cuando este valida
 * la información de la firma
 *
 * @param handler
 */
export function onValidation (handler: (validation: SignatureValidation) => void) {
    return subscribe(Event.OnValidation, ([data]) => {
        if (!_.isObject(data)) {
            throw new Error('El formato de la información de validación no es válido')
        }

        handler(SignatureValidation.deserialize(data as KeyValuePair))
    })
}

/**
 * Elimina la suscripción activa al evento argumentado si existe alguna.
 *
 * @param event
 */
export async function unsubscribe (event: Event) {
    return wamp.unsubscribe(uri(event))
}

/**
 * Elimina la suscripción de validación
 */
export function removeOnValidation () {
   return unsubscribe(Event.OnValidation)
}

export function removeOnClear () {
    return unsubscribe(Event.Clear)
}

/**
 * Elimina todas las suscripciones y eventos
 */
export async function clear () {
   return wamp.clear()
}

export function onClose(handler: (reason: string) => void) {
    wamp.onClose(handler)
}

export function onOpen (handler: () => void) {
    wamp.onOpen(handler)
}
